import { EProductFormat } from '@/domain/product/EProductFormat';
import { postPdfExport } from '@/api/pdf.api';
import { saveToLocalStorage } from '@/utils/localStorageSave';
import { useConfigStore } from '@/store/config.store';
import { useLoadedProduct } from '@/composables/useLoadedProduct';
import { useEditor2D } from '@/composables/useEditor2D';
import { useTemplateStore } from '@/store/template.store';
import { storeToRefs } from 'pinia';
import type { ICart } from '@/typescript/interfaces/ICart';

// Composable rather than application/ use-case because it needs
// stores + api + the useEditor2D composable — forbidden by the
// application/ ESLint boundary. Safe to call outside setup: the
// composables it uses have module-level caches.

function useAddToCart() {
    async function addToCart(projectId?: string): Promise<ICart> {
        const rectoEditor = useEditor2D('recto');
        if (!rectoEditor) {
            throw new Error('useAddToCart: recto editor is not mounted');
        }

        const templateStore = useTemplateStore();
        const { isAdmin } = storeToRefs(useConfigStore());
        const product = useLoadedProduct();

        if (!isAdmin.value && projectId) {
            await saveToLocalStorage(templateStore.selectedTemplate, projectId);
        }

        const blobRecto = await rectoEditor.exports.exportPrintBlob();
        if (!blobRecto) {
            throw new Error('useAddToCart: failed to export recto print blob');
        }

        const isRectoVerso = product.value.format === EProductFormat.RECTO_VERSO;
        const versoEditor = isRectoVerso ? useEditor2D('verso') : null;

        let blobVerso: Blob | null = null;
        let batPreviewVerso: Blob | undefined;
        if (versoEditor) {
            blobVerso = await versoEditor.exports.exportPrintBlob();
        }

        const batPreview = await rectoEditor.exports.exportWatermarkedPreview();
        if (!batPreview) {
            throw new Error('useAddToCart: failed to build watermarked recto preview');
        }
        if (blobVerso && versoEditor) {
            batPreviewVerso = await versoEditor.exports.exportWatermarkedPreview();
        }

        let error = false;
        let errorMessage = '';
        let pdf = new Blob();
        let templateId = '';
        let thumbnail = new Blob();
        let thumbnailVerso = new Blob();

        try {
            const rectoThumb = await rectoEditor.exports.exportThumbnail({ width: 224 });
            if (!rectoThumb) {
                throw new Error('useAddToCart: failed to create recto thumbnail');
            }
            thumbnail = rectoThumb;
            templateId = templateStore.selectedTemplate.id || '';

            if (batPreviewVerso && versoEditor) {
                const versoThumb = await versoEditor.exports.exportThumbnail({ width: 224 });
                if (!versoThumb) {
                    throw new Error('useAddToCart: failed to create verso thumbnail');
                }
                thumbnailVerso = versoThumb;
            }
            pdf = await postPdfExport(product.value.width, product.value.height, blobRecto, blobVerso || undefined);
        } catch (e) {
            error = true;
            errorMessage = `${e}`;
        }

        return {
            batPreview,
            batPreviewVerso,
            error,
            error_message: errorMessage,
            pdf,
            project_id: projectId ?? '',
            template_id: templateId,
            thumbnail,
            thumbnailVerso,
        };
    }

    async function debugCartPdf(projectId?: string): Promise<void> {
        const cart = await addToCart(projectId);
        if (cart.error) {
            console.error('useAddToCart: failed to generate PDF:', cart.error_message);
            return;
        }
        const pdfBlob = cart.pdf;
        if (pdfBlob && pdfBlob.size > 0) {
            const pdfUrl = URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = pdfUrl;
            link.download = 'bat.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(pdfUrl);
        }
    }

    return { addToCart, debugCartPdf };
}

export { useAddToCart };
